import { useEffect, useRef, useState } from 'react';
import { Note } from '@wyocspay/ui';

/**
 * Crop and downscale a chosen logo in the browser, before it ever leaves the
 * page.
 *
 * Why this exists: a merchant should be able to pick any image - a big export, a
 * screenshot, a photo of a sign - and end up with a small, well-cropped logo.
 * The source file is read locally (an object URL, never uploaded raw), the
 * merchant frames it, and only the cropped result is encoded and sent. That
 * result is deliberately small: it is shown at around forty pixels beside the
 * business name and rides inline on the payment page's first paint, so the
 * output is capped to a modest dimension and re-compressed until it fits the
 * byte budget the API enforces. WebP where the browser can make it (smaller,
 * keeps transparency), PNG as the fallback.
 *
 * The crop is free-form on purpose: a wide wordmark and a square badge are both
 * logos, and forcing either into a fixed aspect would ruin one of them.
 */

/** Longest side of the saved logo, in pixels. Crisp on the terminal, tiny on the wire. */
const OUTPUT_MAX_DIM = 512;
/** A little under the API's 256 KB MAX_LOGO_BYTES; the encoder shrinks until it fits. */
const OUTPUT_MAX_BYTES = 240 * 1024;
/** Smallest crop rectangle, in on-screen pixels, so a corner is always grabbable. */
const MIN_CROP = 28;

type Handle = 'move' | 'nw' | 'ne' | 'sw' | 'se';
interface Rect { x: number; y: number; w: number; h: number }

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error('The cropped image could not be read.'));
    reader.readAsDataURL(blob);
  });
}

/** Does this browser's canvas actually produce WebP, or should we fall back to PNG? */
function webpSupported(): boolean {
  try {
    return document.createElement('canvas').toDataURL('image/webp').startsWith('data:image/webp');
  } catch {
    return false;
  }
}

export function LogoCropModal({
  file,
  onSave,
  onCancel,
}: {
  file: File;
  /** Receives the cropped image as raw base64 (no data: prefix), ready for the API. */
  onSave: (base64: string) => void | Promise<void>;
  onCancel: () => void;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const stageBoxRef = useRef<HTMLDivElement | null>(null);
  const drag = useRef<{ handle: Handle; px: number; py: number; start: Rect } | null>(null);

  const [nat, setNat] = useState<{ w: number; h: number } | null>(null);
  const [stage, setStage] = useState<{ w: number; h: number } | null>(null);
  const [rect, setRect] = useState<Rect | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The source travels as an object URL so the raw (possibly large) file is
  // never turned into a base64 string in memory; only the small crop is.
  useEffect(() => {
    const url = URL.createObjectURL(file);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Focus the dialog for keyboard users; Escape cancels (never while saving).
  useEffect(() => {
    dialogRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) { e.preventDefault(); onCancel(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  function onImgLoad(): void {
    const img = imgRef.current;
    if (!img) return;
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    if (!nw || !nh) { setError('That image could not be read.'); return; }
    setNat({ w: nw, h: nh });
    const maxW = Math.min(560, Math.max(240, window.innerWidth - 96));
    const maxH = Math.min(380, Math.max(200, window.innerHeight - 300));
    const scale = Math.min(maxW / nw, maxH / nh, 1);
    const w = Math.max(1, Math.round(nw * scale));
    const h = Math.max(1, Math.round(nh * scale));
    setStage({ w, h });
    const cw = Math.round(w * 0.8);
    const ch = Math.round(h * 0.8);
    setRect({ x: Math.round((w - cw) / 2), y: Math.round((h - ch) / 2), w: cw, h: ch });
  }

  function beginDrag(handle: Handle) {
    return (e: React.PointerEvent) => {
      if (!rect || busy) return;
      e.preventDefault();
      e.stopPropagation();
      (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
      drag.current = { handle, px: e.clientX, py: e.clientY, start: { ...rect } };
    };
  }

  function onPointerMove(e: React.PointerEvent): void {
    const d = drag.current;
    if (!d || !stage) return;
    const dx = e.clientX - d.px;
    const dy = e.clientY - d.py;
    const s = d.start;
    const right = s.x + s.w;
    const bottom = s.y + s.h;
    let next: Rect;
    if (d.handle === 'move') {
      next = { x: clamp(s.x + dx, 0, stage.w - s.w), y: clamp(s.y + dy, 0, stage.h - s.h), w: s.w, h: s.h };
    } else {
      let x = s.x; let y = s.y; let w = s.w; let h = s.h;
      if (d.handle === 'nw') { x = clamp(s.x + dx, 0, right - MIN_CROP); y = clamp(s.y + dy, 0, bottom - MIN_CROP); w = right - x; h = bottom - y; }
      if (d.handle === 'ne') { y = clamp(s.y + dy, 0, bottom - MIN_CROP); w = clamp(s.w + dx, MIN_CROP, stage.w - s.x); h = bottom - y; }
      if (d.handle === 'sw') { x = clamp(s.x + dx, 0, right - MIN_CROP); w = right - x; h = clamp(s.h + dy, MIN_CROP, stage.h - s.y); }
      if (d.handle === 'se') { w = clamp(s.w + dx, MIN_CROP, stage.w - s.x); h = clamp(s.h + dy, MIN_CROP, stage.h - s.y); }
      next = { x, y, w, h };
    }
    setRect(next);
  }

  function endDrag(): void { drag.current = null; }

  // Keyboard nudge for accessibility (arrows move, shift = larger step).
  function onRectKey(e: React.KeyboardEvent): void {
    if (!rect || !stage) return;
    const step = e.shiftKey ? 10 : 1;
    let { x, y } = rect;
    if (e.key === 'ArrowLeft') x = clamp(x - step, 0, stage.w - rect.w);
    else if (e.key === 'ArrowRight') x = clamp(x + step, 0, stage.w - rect.w);
    else if (e.key === 'ArrowUp') y = clamp(y - step, 0, stage.h - rect.h);
    else if (e.key === 'ArrowDown') y = clamp(y + step, 0, stage.h - rect.h);
    else return;
    e.preventDefault();
    setRect({ ...rect, x, y });
  }

  async function encode(img: HTMLImageElement, r: Rect): Promise<string> {
    const back = nat!.w / stage!.w; // on-screen -> source pixels
    const sx = clamp(Math.round(r.x * back), 0, nat!.w);
    const sy = clamp(Math.round(r.y * back), 0, nat!.h);
    const sw = clamp(Math.round(r.w * back), 1, nat!.w - sx);
    const sh = clamp(Math.round(r.h * back), 1, nat!.h - sy);
    const fit = Math.min(1, OUTPUT_MAX_DIM / Math.max(sw, sh));
    let w = Math.max(1, Math.round(sw * fit));
    let h = Math.max(1, Math.round(sh * fit));
    const type = webpSupported() ? 'image/webp' : 'image/png';
    let quality = 0.92;

    for (let attempt = 0; attempt < 8; attempt += 1) {
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Image editing is not supported in this browser.');
      ctx.clearRect(0, 0, w, h);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, w, h);
      const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, type, quality));
      if (!blob) throw new Error('The image could not be exported.');
      if (blob.size <= OUTPUT_MAX_BYTES) return blobToBase64(blob);
      // Too big: lower quality first (WebP/JPEG honour it), then shrink pixels.
      if (type !== 'image/png' && quality > 0.55) quality -= 0.13;
      else { w = Math.max(1, Math.round(w * 0.85)); h = Math.max(1, Math.round(h * 0.85)); }
    }
    throw new Error('This image is too detailed to fit the size limit. Try a tighter crop.');
  }

  async function save(): Promise<void> {
    if (!rect || !nat || !stage || !imgRef.current) return;
    setBusy(true);
    setError(null);
    try {
      const base64 = await encode(imgRef.current, rect);
      await onSave(base64);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not process the image.');
      setBusy(false);
    }
  }

  const handleStyle: React.CSSProperties = {
    position: 'absolute', width: 16, height: 16, background: '#fff',
    border: '2px solid var(--wc-blue)', boxSizing: 'border-box', touchAction: 'none',
  };
  // A generous transparent hit area around each visible handle for easier grabbing.
  const hitPad = 12;

  return (
    <div
      role="presentation"
      onPointerDown={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <style>{`
        @keyframes wc-crop-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
        @media (prefers-reduced-motion: reduce) { .wc-crop-dialog { animation: none !important; } }
      `}</style>
      <div
        ref={dialogRef}
        className="wc-crop-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Crop your logo"
        tabIndex={-1}
        style={{
          width: '100%', maxWidth: 640, background: 'var(--wc-surface)',
          border: '1px solid var(--wc-border)', boxShadow: 'var(--wc-shadow, 0 10px 40px rgba(0,0,0,0.25))',
          outline: 'none', animation: 'wc-crop-in 160ms ease-out',
          maxHeight: '92vh', overflow: 'auto',
        }}
      >
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--wc-border)' }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--wc-ink)' }}>Crop your logo</h2>
          <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--wc-muted)', lineHeight: 1.5 }}>
            Drag the frame to move it, or a corner to resize. It is saved at the crop you choose and
            optimized automatically.
          </p>
        </div>

        <div style={{ padding: 20, display: 'grid', gap: 14, justifyItems: 'center' }}>
          {!stage || !rect ? (
            <div style={{ height: 200, display: 'flex', alignItems: 'center', color: 'var(--wc-faint)', fontSize: 13 }}>
              Loading image…
            </div>
          ) : null}

          {src ? (
            <div
              ref={stageBoxRef}
              onPointerMove={onPointerMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              style={{
                position: 'relative', width: stage?.w, height: stage?.h,
                // A checkerboard so transparent PNG edges are visible while cropping.
                background:
                  'repeating-conic-gradient(#e9edf0 0% 25%, #f7f9fa 0% 50%) 50% / 18px 18px',
                touchAction: 'none', userSelect: 'none', display: stage ? 'block' : 'none',
                maxWidth: '100%',
              }}
            >
              <img
                ref={imgRef}
                src={src}
                alt=""
                onLoad={onImgLoad}
                draggable={false}
                style={{ display: 'block', width: stage?.w, height: stage?.h, pointerEvents: 'none' }}
              />

              {rect ? (
                <>
                  {/* The crop frame. The huge spread box-shadow dims everything
                      outside it, so the kept area is obvious without four masks. */}
                  <div
                    role="button"
                    tabIndex={0}
                    aria-label="Crop area. Use arrow keys to move."
                    onPointerDown={beginDrag('move')}
                    onKeyDown={onRectKey}
                    style={{
                      position: 'absolute', left: rect.x, top: rect.y, width: rect.w, height: rect.h,
                      boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)',
                      border: '1px solid var(--wc-blue)', cursor: 'move', touchAction: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                  {(['nw', 'ne', 'sw', 'se'] as const).map((h) => {
                    const pos: React.CSSProperties =
                      h === 'nw' ? { left: rect.x, top: rect.y } :
                      h === 'ne' ? { left: rect.x + rect.w, top: rect.y } :
                      h === 'sw' ? { left: rect.x, top: rect.y + rect.h } :
                      { left: rect.x + rect.w, top: rect.y + rect.h };
                    const cursor = h === 'nw' || h === 'se' ? 'nwse-resize' : 'nesw-resize';
                    return (
                      <div
                        key={h}
                        onPointerDown={beginDrag(h)}
                        aria-hidden
                        style={{
                          position: 'absolute', left: (pos.left as number) - hitPad, top: (pos.top as number) - hitPad,
                          width: 16 + hitPad * 2, height: 16 + hitPad * 2, padding: hitPad,
                          boxSizing: 'border-box', cursor, touchAction: 'none',
                        }}
                      >
                        <div style={{ ...handleStyle, position: 'static' }} />
                      </div>
                    );
                  })}
                </>
              ) : null}
            </div>
          ) : null}

          {error ? <div style={{ width: '100%' }}><Note tone="danger">{error}</Note></div> : null}
        </div>

        <div style={{ padding: '14px 20px', borderTop: '1px solid var(--wc-border)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="wc-btn wc-btn-secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="wc-btn" onClick={() => void save()} disabled={busy || !rect}>
            {busy ? 'Saving…' : 'Save logo'}
          </button>
        </div>
      </div>
    </div>
  );
}
